/* Batch 14 · M15 — the engagement backup package (.azex): a local safety copy of the audit work, re-loadable only into
   AZEX. Build runs as a background job; the download asks for a fresh one-time code (the 428 step-up flow sends it to the
   user's own e-mail) and is on the Audit Trail with the package hash. The client ledger and client files are NOT in the
   package — the dialog says so. Any role on the engagement, at any stage (open or Final). */
import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTimeFirm } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { toast } from "sonner";
import { capJobText } from "@/lib/engagementCap";   // Pre-Step 4 · 4b: a refused job shows the cap text (EN / AR)
import { Download, Package } from "lucide-react";
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

export default function EngagementPackageDialog({ eng, open, onOpenChange }) {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!eng) return;
    try { const r = await api.get(`/engagements/${eng.id}/packages`, { _silent: true }); setList(asArray(r.data?.packages)); } catch { setList([]); }
  }, [eng]);
  useEffect(() => { if (open) { setJob(null); setBusy(false); load(); } return () => { if (timer.current) clearTimeout(timer.current); }; }, [open, load]);

  const poll = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/jobs/${id}`, { _silent: true });
        setJob(r.data);
        if (r.data.status === "done") { setBusy(false); toast.success(t("pkg.ready")); await load(); return; }
        if (r.data.status === "failed") { setBusy(false); toast.error(t("pkg.failed")); return; }
      } catch (_) { /* keep polling */ }
      poll(id);
    }, 1200);
  };
  const build = async () => {
    setBusy(true);
    try { const r = await api.post(`/engagements/${eng.id}/packages`); setJob({ id: r.data.job_id, status: "queued", progress_pct: 0 }); poll(r.data.job_id); }
    catch (e) { setBusy(false); toast.error(toErrMsg(e, t("pkg.failed"))); }
  };
  const download = async (p) => {
    try {
      const tk = await api.post(`/engagements/${eng.id}/packages/${p.id}/download-ticket`);   // the fresh one-time code (428 → step-up dialog)
      const r = await api.get(`/engagements/${eng.id}/packages/${p.id}/download`, { responseType: "blob", params: { ticket: tk.data?.ticket } });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = p.filename || "engagement.azex"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("pkg.downloaded"));
    } catch (e) { toast.error(toErrMsg(e, t("pkg.failed"))); }
  };
  const pct = Math.max(0, Math.min(100, Number(job?.progress_pct || 0)));
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="pkg-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package className="h-4 w-4" />{t("pkg.title")}</DialogTitle>
          <DialogDescription>{t("pkg.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded border bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="pkg-excluded">{t("pkg.excluded")}</div>
          <div className="text-xs text-muted-foreground">{t("pkg.security")}</div>
          {job && (
            <div data-testid="pkg-progress">
              <div className="h-2 w-full rounded bg-slate-200"><div className={`h-2 rounded ${job.status === "failed" ? "bg-red-600" : "bg-emerald-600"}`} style={{ width: `${job.status === "done" ? 100 : pct}%` }} /></div>
              <div className="mt-1 text-xs text-muted-foreground" data-testid="pkg-line">{job.status === "done" ? t("pkg.ready") : job.status === "failed" ? t("pkg.failed") : (job.message || t("pkg.queued"))} {job.status !== "done" && job.status !== "failed" ? `· ${pct}%` : ""}</div>
            </div>
          )}
          <div>
            <div className="mb-1 text-xs font-semibold">{t("pkg.list")}</div>
            {list.length === 0 ? <div className="text-xs text-muted-foreground" data-testid="pkg-empty">{t("pkg.none")}</div> : (
              <table className="w-full text-[12px]" data-testid="pkg-table">
                <tbody className="divide-y">{list.map((p) => (
                  <tr key={p.id} data-testid={`pkg-row-${p.id}`}>
                    <td className="py-1 font-mono">{p.filename}</td>
                    <td className="py-1 text-xs text-muted-foreground">{fmtDateTimeFirm(p.created_at)} · {p.created_by_name}{p.locked ? ` · ${t("pkg.lockedFile")}` : ""}</td>
                    <td className="py-1 text-end"><Button size="sm" variant="outline" className="h-7" onClick={() => download(p)} data-testid={`pkg-download-${p.id}`}><Download className="me-1 h-3 w-3" />{t("pkg.download")}</Button></td>
                  </tr>))}</tbody>
              </table>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button disabled={busy} onClick={build} data-testid="pkg-build">{busy ? t("pkg.building") : t("pkg.build")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Restore: upload a .azex into the user's own firm → a NEW engagement (never an overwrite). */
export function RestorePackageButton({ onRestored }) {
  const { t, lang } = useI18n();
  const [job, setJob] = useState(null);
  const inputRef = useRef(null);
  const timer = useRef(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const poll = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/jobs/${id}`, { _silent: true });
        setJob(r.data);
        if (r.data.status === "done") { toast.success(t("pkg.restored").replace("{name}", r.data.result?.name || "")); onRestored && onRestored(r.data.result || {}); setJob(null); return; }
        if (r.data.status === "failed") { toast.error(`${t("pkg.restoreFailed")}: ${capJobText(r.data, lang) || jobErrorText(r.data, lang, t)}`); setJob(null); return; }
      } catch (_) { /* keep polling */ }
      poll(id);
    }, 1200);
  };
  const upload = async (f) => {
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/engagements/packages/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob({ id: r.data.job_id, status: "queued" }); poll(r.data.job_id);
    } catch (e) { toast.error(toErrMsg(e, t("pkg.restoreFailed"))); }
    finally { if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept=".azex" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="pkg-restore-input" />
      <Button size="sm" variant="outline" disabled={!!job} onClick={() => inputRef.current?.click()} data-testid="pkg-restore-btn">
        <Package className="me-1 h-3.5 w-3.5" />{job ? t("pkg.restoring") : t("pkg.restore")}
      </Button>
    </>
  );
}
